/**
 * Main App
 */
import React, { useCallback, useEffect, useState } from "react";

import MomentUtils from "@date-io/moment";
import { CircularProgress, StyledEngineProvider, ThemeProvider, Theme } from "@mui/material";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { Toaster } from "Components/Generic/Toaster";
import LoadingScreen from "Components/LoadingScreen";
import ReactDOM from "react-dom";
import { useTranslation } from "react-i18next";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import { getDomainWithoutSuffix, getSubdomain } from "tldts";

import "./assets/scss/shipeezi-grid.scss";
import "./lib/shipeeziCss";

import Leave from "./components/Generic/Modal/Leave";
import FCMInitializer from "./components/Notifications/FCMInitializer";
import { IGNORE_SUB_DOMAINS } from "./constants";
import { MainApp } from "./container/App";
import { CustomerPortalApp } from "./container/CustomerPortalApp";
import { useAppState } from "./context/StateContext";
import CompanySubDomainService from "./services/company.subdomain.service";
import { LoadingContainer } from "./styles";
import { i18nInit } from "./translate";
import { createQueryClient } from "./util/app/createQueryClient";
import { initTimeAgo } from "./util/app/initTimeAgo";
import { AppType } from "./util/enums";
import env from "./util/env";
import ScrollToTop from "./util/ScrollToTop";
import { appTheme } from "Themes/appTheme";
import { LocalizationProvider } from "@mui/x-date-pickers";

const ApplicationContent = () => {
  let subDomain: string | null;

  if (["work", "test", "demo", "production"].includes(env.NODE_ENV || "")) {
    subDomain = getSubdomain(window.location.origin);
  } else {
    subDomain = getDomainWithoutSuffix(window.location.origin);
  }

  const { subDomainInfo, setSubDomainInfo } = useAppState();

  const { t: translate } = useTranslation();

  const [appTypeByDomain, setAppTypeByDomain] = useState<number>();

  const queryClient = createQueryClient(translate);

  const updateSubDomainInfo = async (subDomain: string) => {
    const subDomainResponse = await CompanySubDomainService.getBy(subDomain);
    const { data: subDomainInfo } = subDomainResponse;

    if (subDomainInfo && typeof subDomainInfo === "object") {
      setSubDomainInfo(subDomainInfo);

      return subDomainInfo;
    }

    setSubDomainInfo(undefined);

    return null;
  };

  const getRouteByDomain = async () => {
    if (subDomain && !IGNORE_SUB_DOMAINS.includes(subDomain)) {
      if (subDomainInfo) {
        setAppTypeByDomain(AppType.CUSTOMER_PORTAL);
        await updateSubDomainInfo(subDomain);
      } else {
        try {
          const subDomainInfo = await updateSubDomainInfo(subDomain);

          if (subDomainInfo) {
            setAppTypeByDomain(AppType.CUSTOMER_PORTAL);
          } else {
            setAppTypeByDomain(AppType.DEFAULT);
          }
        } catch (error) {
          setAppTypeByDomain(AppType.DEFAULT);
        }
      }
    } else {
      setAppTypeByDomain(AppType.DEFAULT);
    }
  };

  const getAppRoutes = () => {
    switch (appTypeByDomain) {
      case AppType.CUSTOMER_PORTAL:
        return <Route path="/" component={CustomerPortalApp} />;
      default:
        return <Route path="/" component={MainApp} />;
    }
  };

  useEffect(() => {
    getRouteByDomain();
    initTimeAgo(translate);
  }, []);

  return (
    <StyledEngineProvider injectFirst>
      <ThemeProvider theme={appTheme}>
        <Toaster />

        <QueryClientProvider client={queryClient}>
          <FCMInitializer />
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Router
              getUserConfirmation={(message, callback) => {
                const modal = document.createElement("div");

                document.body.appendChild(modal);

                const closeModal = (redirect: boolean) => {
                  ReactDOM.unmountComponentAtNode(modal);
                  document.body.removeChild(modal);
                  callback(redirect);
                };

                ReactDOM.render(
                  <ThemeProvider theme={appTheme}>
                    <Leave open onClose={(isConfirmed) => closeModal(isConfirmed)} />
                  </ThemeProvider>,
                  modal
                );
              }}
            >
              <ScrollToTop />
              {appTypeByDomain === undefined ? (
                <LoadingContainer>
                  <CircularProgress />
                </LoadingContainer>
              ) : (
                <Switch>{getAppRoutes()}</Switch>
              )}
            </Router>
          </LocalizationProvider>
          <ReactQueryDevtools initialIsOpen={false} position="bottom-right" />
        </QueryClientProvider>
      </ThemeProvider>
    </StyledEngineProvider>
  );
};

export const Application = () => {
  const { activeLanguageId, defaultLanguage } = useAppState();

  const [translateIsReady, setTranslateIsReady] = useState(false);

  useEffect(() => {
    loadTranslate();
  }, []);

  const loadTranslate = useCallback(async () => {
    await i18nInit(activeLanguageId, defaultLanguage);
    setTranslateIsReady(true);
  }, []);

  const renderContent = () => {
    if (!translateIsReady) return <LoadingScreen showing message="" />;

    return <ApplicationContent />;
  };

  return renderContent();
};
